import type { LucideIcon } from 'lucide-react'
import { AlertCircle, AlertTriangle, CheckCircle, XCircle } from 'lucide-react'

export interface HealthStatus {
  label: 'Healthy' | 'Moderate' | 'Risky' | 'Liquidation'
  color: string
  icon: LucideIcon
}

/**
 * Determines health status based on health factor value.
 * Used in dialogs for displaying position health with color and icon.
 *
 * Thresholds:
 * - Healthy: HF > 3 or infinite
 * - Moderate: HF >= 1.5
 * - Risky: HF >= 1
 * - Liquidation: HF < 1
 */
export function getHealthStatus(hf: number): HealthStatus {
  if (!Number.isFinite(hf) || hf > 3) return { label: 'Healthy', color: 'text-green-400', icon: CheckCircle }
  if (hf >= 1.5) return { label: 'Moderate', color: 'text-amber-400', icon: AlertCircle }
  if (hf >= 1) return { label: 'Risky', color: 'text-red-400', icon: AlertTriangle }
  return { label: 'Liquidation', color: 'text-red-400', icon: XCircle }
}

/**
 * Formats health factor value for display.
 * Returns '>100' for very large values, 'Liquidation' for zero, otherwise 2 decimal places.
 */
export function formatHealthFactorValue(hf: number): string {
  if (!Number.isFinite(hf) || hf > 100) return '>100'
  if (hf === 0) return 'Liquidation'
  return hf.toFixed(2)
}

/**
 * Formats LTV (Loan-to-Value) percentage for display.
 * Returns '∞' for infinite values, otherwise formatted with 2 decimal places and % suffix.
 */
export function formatLTV(ltv: number): string {
  if (!Number.isFinite(ltv)) return '∞'
  return `${ltv.toFixed(2)}%`
}
